//Limitless imports
import { log as pilog } from "common/utils/Logging";
import { uniqueArrayBy } from "common/utils/array-helper/ArrayHelper";
export const TEXT_ENCODING = "text";
export const BUFFER_ENCODING = "buffer";
export const BASE64_ENCODING = "base64";
export const HTML_ENCODING = "html";
export const UI_LABELS_JSON = {
  name: "ui_labels.json",
  encoding: TEXT_ENCODING,
  stateKey: "uilabels",
  defaultContent: null
};
export const QUALITY_REVIEW_JSON = {
  name: "review_criteria.json",
  encoding: TEXT_ENCODING,
  stateKey: "defaultReviewCriteria",
  defaultContent: null
};
export const CONFIG_JSON = {
  name: "config.json",
  encoding: TEXT_ENCODING,
  stateKey: "llmConfig",
  defaultContent: null
};
export const THEME_JSON = {
  name: "theme.json",
  encoding: TEXT_ENCODING,
  stateKey: "theme",
  defaultContent: null
};
export const UPGRADE_JSON = {
  name: "upgrade.json",
  encoding: TEXT_ENCODING,
  ignoreCache: true,
  stateKey: "upgradeContent",
  defaultContent: null,
  localeOverride: "en-GB" //upgrade content now comes from a central file
};
export const WHITE_LIST_CONTENT = {
  name: "whiteList.txt",
  encoding: TEXT_ENCODING,
  processor: HTML_ENCODING,
  stateKey: "whiteListResource",
  defaultContent: "<p> To join Limitless you need to be invited. If you have been invited but are having trouble registering, please email our friendly team at <a href='https://www.limitlesstech.com/expert-portal#ambassador-form' target='_blank'>https://www.limitlesstech.com/expert-portal#ambassador-form</a> </p>"
};
export const EXPERT_TERMS = {
  name: "terms/expert_terms.html",
  encoding: BUFFER_ENCODING
};
export const PRIVACY_POLICY = {
  name: "terms/expert_privacy_policy.html",
  encoding: BUFFER_ENCODING
};
export const CODE_OF_CONDUCT = {
  name: "terms/code_of_conduct.html",
  encoding: BUFFER_ENCODING
};
export const INTERNATIONAL_DATA_TRANSFER_ADDENDUM = {
  name: "terms/international_data_transfer_addendum.html",
  encoding: BUFFER_ENCODING
};
export const TUTORIAL_JSON = {
  name: "helpTutorials.json",
  encoding: TEXT_ENCODING
};
export const TOWNS_JSON = {
  name: "towns.json",
  encoding: TEXT_ENCODING,
  stateKey: "towns",
  defaultContent: []
};
export const HOW_IT_WORKS_JSON = {
  name: "llm_how_it_works.json",
  encoding: TEXT_ENCODING,
  stateKey: "hiwSlides"
};
export const HOW_IT_WORKS = {
  name: "how_this_works.txt",
  encoding: TEXT_ENCODING,
  processor: HTML_ENCODING,
  stateKey: "learnmoreContent",
  defaultContent: ""
};
export const MARKETING_CARDS = {
  name: "marketing_cards.json",
  encoding: TEXT_ENCODING,
  stateKey: "marketingContent",
  formatRemoteContent: (content, defaultContent) => {
    const newMarketingContent = [];
    //map over the platform content
    content.map(platformContent => {
      //find the local content for this card (if available)
      const card = defaultContent.find(content => content.id === platformContent.id);
      //push the card to the new marketing content
      newMarketingContent.push({
        ...card,
        ...platformContent
      });
    });
    //merge the cards array together in case more are defined in platform
    const finalMarketingContent = uniqueArrayBy([...newMarketingContent, ...defaultContent], "id");
    return finalMarketingContent;
  }
};
export const DISCLAIMER = {
  name: "disclaimer.txt",
  encoding: TEXT_ENCODING,
  processor: HTML_ENCODING,
  stateKey: "disclaimerContent",
  defaultContent: ""
};
export const REPORT = {
  name: "report.txt",
  encoding: TEXT_ENCODING,
  processor: HTML_ENCODING,
  stateKey: "reportContent",
  defaultContent: "<h4>Would you like to report this question?</h4>"
};
export const BRAND_IMG = {
  name: "brand.png",
  encoding: BASE64_ENCODING,
  processor: BASE64_ENCODING,
  stateKey: "brandImage",
  //this is a 1px x 1px white pixel
  defaultContent: "data:image/*;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
  formatRemoteContent: content => `data:image/*;base64,${content}`
};
export const STATIC_TEXT_EVENT_MAPPING = {
  name: "staticTextEventMapping.json",
  encoding: TEXT_ENCODING,
  stateKey: "staticTextEventMapping",
  defaultContent: [],
  formatRemoteContent: content => content.eventTextMapping
};
export const BRAND_ASSETS = {
  name: "brandAssets.json",
  encoding: TEXT_ENCODING,
  stateKey: "tenantBrandAssets",
  defaultContent: {},
  formatRemoteContent: content => content.brandAssets
};
export const CRUI_APP_RESOURCES = [UI_LABELS_JSON, THEME_JSON, HOW_IT_WORKS, MARKETING_CARDS, DISCLAIMER, BRAND_IMG, REPORT];
export const LLM_RESOURCES = [UI_LABELS_JSON, CONFIG_JSON, THEME_JSON, HOW_IT_WORKS_JSON, UPGRADE_JSON];
export const EWA_APP_RESOURCES = [UI_LABELS_JSON, THEME_JSON, UPGRADE_JSON, WHITE_LIST_CONTENT, TOWNS_JSON];
export const EMA_APP_RESOURCES = [UI_LABELS_JSON, UPGRADE_JSON, THEME_JSON, WHITE_LIST_CONTENT, TOWNS_JSON];
//not currently used, but we should
export const ASYNC_EMA_APP_RESOURCES = [THEME_JSON, WHITE_LIST_CONTENT, TOWNS_JSON];
export const PORTAL_RESOURCES = [UI_LABELS_JSON, THEME_JSON];
export const TERMS_RESOURCES = [EXPERT_TERMS, PRIVACY_POLICY];
export const ONBOARD_TERMS_RESOURCES = [EXPERT_TERMS, PRIVACY_POLICY, CODE_OF_CONDUCT];

//this is here for coaching card editor. really we should load the locale specific towns when editing cards
export const CROWD_MANAGER_RESOURCES = [{
  ...UI_LABELS_JSON,
  localeOverride: "en-GB"
}, {
  ...QUALITY_REVIEW_JSON,
  localeOverride: "en-GB"
}, TOWNS_JSON];

/**
 * An array of JSON resources to be processed
 *
 * @param {array} resourceArray
 */
export const jsonResourceProcessor = platformResource => {
  const log = pilog.getLogger("RemoteResourceUtil jsonResourceProcessor");
  const {
    content,
    fileName,
    location,
    resourceName,
    applicationName
  } = platformResource;
  const resource = {
    resourceName: resourceName ? resourceName : fileName,
    fileName: fileName ? fileName : resourceName,
    location,
    applicationName
  };
  if (platformResource.content) {
    try {
      resource.content = JSON.parse(content);
    } catch (error) {
      log.error(`Failed to process ${fileName ? fileName : resourceName} %o`, error);
    }
  }
  return resource;
};

/**
 * An array of buffer stream resources to be processed
 *
 * @param {array} resourceArray
 */
export const bufferResourceProcessor = platformResource => {
  const log = pilog.getLogger("RemoteResourceUtil bufferResourceProcessor");
  const resource = {
    resourceName: platformResource.resourceName,
    location: platformResource.location,
    fileName: platformResource.fileName
  };
  try {
    if (platformResource.content) {
      const bytes = new Uint8Array(platformResource.content.data);
      const decoder = new TextDecoder("utf-8");
      resource.content = decoder.decode(bytes);
    }
  } catch (error) {
    log.error(`Unable to decode ${platformResource.resourceName} from the platform content %o`, error);
  }
  return resource;
};
export const RESOURCE_PROCESSOR_MAP = {
  [TEXT_ENCODING]: jsonResourceProcessor,
  [BUFFER_ENCODING]: bufferResourceProcessor,
  [HTML_ENCODING]: resource => resource,
  [BASE64_ENCODING]: resource => resource
};